/**
 * Shift status vocabulary — single source of truth for the shift-status visual
 * signal (icon + label) shared across the calendar shift surfaces.
 *
 * Two consumers style shift status differently:
 *   - `ShiftBlock` (EventBlock.tsx) styles the whole time block.
 *   - `ShiftRosterBlock` styles small row badges + avatar chips.
 *
 * Their *layout* classes diverge (block background vs. inline badge/chip), so
 * this module deliberately captures only what is genuinely shared: the
 * per-status Lucide icon and canonical label. Each consumer applies these to
 * its own layout and supplies its own color classes.
 */

import {
  CirclePlus,
  CalendarDays,
  PlayCircle,
  CheckCircle,
  type LucideIcon,
} from 'lucide-react';
import { ShiftStatus, assertNever } from '@signalvote/shared';

export interface ShiftStatusMeta {
  /** Canonical display label for the status. */
  label: string;
  /** The Lucide icon for the status — byte-identical across every shift surface. */
  icon: LucideIcon;
}

/**
 * Icon + label for a shift status.
 *
 * The icon is byte-identical everywhere. The label here is the canonical form.
 * `ShiftRosterBlock` renders a lowercase "In progress" variant for that one
 * status, so it consumes only the shared icon and keeps its own label strings —
 * see its status map. This keeps every rendered label byte-identical while still
 * de-duplicating the icon vocabulary.
 */
export function getShiftStatusMeta(status: ShiftStatus): ShiftStatusMeta {
  switch (status) {
    case ShiftStatus.Available:
      return { label: 'Available', icon: CirclePlus };
    case ShiftStatus.Claimed:
      return { label: 'Claimed', icon: CalendarDays };
    case ShiftStatus.InProgress:
      return { label: 'In Progress', icon: PlayCircle };
    case ShiftStatus.Completed:
      return { label: 'Completed', icon: CheckCircle };
    default:
      return assertNever(status);
  }
}
